// Helpers for rendering rich-text (HTML) job descriptions safely.
// Descriptions are sanitized server-side (bleach) before storage. The client sanitizes
// again with the same allow-list and renders DOM nodes (see <SafeHtml>), never raw HTML.
import DOMPurify from 'dompurify';

const ALLOW = {
  ALLOWED_TAGS: ['p', 'br', 'b', 'strong', 'i', 'em', 'u', 's', 'strike', 'ul', 'ol', 'li',
    'h2', 'h3', 'h4', 'blockquote', 'span', 'a', 'div'],
  ALLOWED_ATTR: ['href', 'title'],
};

DOMPurify.addHook('afterSanitizeAttributes', (node) => {
  if (node.tagName === 'A') {
    node.setAttribute('target', '_blank');
    node.setAttribute('rel', 'noopener noreferrer nofollow');
  }
});

export const hasHtml = (s) => /<[a-z][\s\S]*>/i.test(s || '');

const escapeText = (s) =>
  String(s)
    .replace(/&/g, '&amp;')
    .replace(/</g, '&lt;')
    .replace(/>/g, '&gt;')
    .replace(/\n/g, '<br/>');

// Sanitized HTML string — only for non-DOM sinks such as JSON-LD.
export const toDisplayHtml = (s) => {
  if (!s) return '';
  return hasHtml(s) ? DOMPurify.sanitize(String(s), ALLOW) : escapeText(s);
};

// Sanitized DOM fragment; plain text becomes text nodes separated by <br>.
export const toSafeFragment = (s) => {
  if (s && hasHtml(s)) return DOMPurify.sanitize(String(s), { ...ALLOW, RETURN_DOM_FRAGMENT: true });
  const frag = document.createDocumentFragment();
  String(s || '').split('\n').forEach((line, i) => {
    if (i) frag.appendChild(document.createElement('br'));
    frag.appendChild(document.createTextNode(line));
  });
  return frag;
};

// JSON for <script type="application/ld+json">: "<" is escaped so data can never close the tag.
export const toJsonLd = (obj) => JSON.stringify(obj).replace(/</g, '\\u003c');

// Plain text (tags stripped) for meta descriptions / previews / char counts.
// Uses DOMParser (does not execute scripts or load resources) instead of innerHTML.
export const toPlainText = (s) => {
  if (!s) return '';
  if (!hasHtml(s)) return String(s);
  if (typeof DOMParser !== 'undefined') {
    const doc = new DOMParser().parseFromString(String(s), 'text/html');
    return (doc.body.textContent || doc.body.innerText || '').trim();
  }
  return String(s).replace(/<[^>]*>/g, ' ').replace(/\s+/g, ' ').trim();
};
